import React, { useEffect, useRef, useState } from "react";
import {
  FiBold,
  FiDroplet,
  FiItalic,
  FiList,
  FiType,
  FiUnderline,
} from "react-icons/fi";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const textColors = [
  { label: "Noir", value: "#0f172a" },
  { label: "Bleu", value: "#0369a1" },
  { label: "Vert", value: "#047857" },
  { label: "Rouge", value: "#be123c" },
  { label: "Orange", value: "#c2410c" },
];

const backgroundColors = [
  { label: "Aucun", value: "transparent" },
  { label: "Jaune", value: "#fef3c7" },
  { label: "Bleu clair", value: "#e0f2fe" },
  { label: "Vert clair", value: "#dcfce7" },
  { label: "Rose clair", value: "#ffe4e6" },
];

function stripHtml(value = "") {
  return String(value)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function ToolbarButton({ active = false, icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      title={label}
      className={cn(
        "flex h-10 w-10 items-center justify-center rounded-xl border text-sm transition",
        active
          ? "border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300"
          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800",
      )}
    >
      <Icon />
    </button>
  );
}

export default function AdminRichTextEditor({
  value,
  onChange,
  error,
  placeholder = "Rédigez votre texte ici...",
}) {
  const editorRef = useRef(null);
  const initializedRef = useRef(false);
  const lastEmittedValueRef = useRef("");

  const [toolbarState, setToolbarState] = useState({
    bold: false,
    italic: false,
    underline: false,
  });

  const normalizedValue = value || "";
  const hasVisibleContent = stripHtml(normalizedValue).length > 0;

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const isFocused = document.activeElement === editor;

    /**
     * Important :
     * - Au premier affichage du drawer, on injecte toujours la valeur reçue.
     * - Ensuite, on évite d’écraser le contenu pendant que l’utilisateur tape,
     *   sinon le curseur peut sauter.
     */
    if (!initializedRef.current) {
      editor.innerHTML = normalizedValue;
      lastEmittedValueRef.current = normalizedValue;
      initializedRef.current = true;
      return;
    }

    if (!isFocused && editor.innerHTML !== normalizedValue) {
      editor.innerHTML = normalizedValue;
      lastEmittedValueRef.current = normalizedValue;
    }
  }, [normalizedValue]);

  const emitChange = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const html = editor.innerHTML;
    lastEmittedValueRef.current = html;
    onChange?.(html);
  };

  const focusEditor = () => {
    editorRef.current?.focus();
  };

  const refreshToolbarState = () => {
    try {
      setToolbarState({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
      });
    } catch {
      setToolbarState({
        bold: false,
        italic: false,
        underline: false,
      });
    }
  };

  const exec = (command, commandValue = null) => {
    focusEditor();
    document.execCommand(command, false, commandValue);
    emitChange();
    refreshToolbarState();
  };

  const handlePaste = (event) => {
    event.preventDefault();

    const text = event.clipboardData.getData("text/plain");
    if (!text) return;

    document.execCommand("insertText", false, text);
    emitChange();
  };

  const handleColorChange = (event) => {
    const color = event.target.value;
    if (!color) return;

    exec("foreColor", color);
    event.target.value = "";
  };

  const handleBackgroundChange = (event) => {
    const color = event.target.value;
    if (!color) return;

    if (color === "transparent") {
      exec("removeFormat");
    } else {
      exec("hiliteColor", color);
    }

    event.target.value = "";
  };

  return (
    <div
      className={cn(
        "overflow-hidden rounded-[1.5rem] border bg-white dark:bg-slate-950",
        error
          ? "border-rose-300 dark:border-rose-500/50"
          : "border-slate-200 dark:border-slate-700",
      )}
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900">
        <ToolbarButton
          icon={FiBold}
          label="Gras"
          active={toolbarState.bold}
          onClick={() => exec("bold")}
        />

        <ToolbarButton
          icon={FiItalic}
          label="Italique"
          active={toolbarState.italic}
          onClick={() => exec("italic")}
        />

        <ToolbarButton
          icon={FiUnderline}
          label="Souligné"
          active={toolbarState.underline}
          onClick={() => exec("underline")}
        />

        <ToolbarButton
          icon={FiList}
          label="Liste à puces"
          onClick={() => exec("insertUnorderedList")}
        />

        <div className="h-8 w-px bg-slate-200 dark:bg-slate-700" />

        <label className="flex min-h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-xs font-bold text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
          <FiType />
          Texte
          <select
            defaultValue=""
            onChange={handleColorChange}
            className="bg-transparent text-xs outline-none"
          >
            <option value="" disabled>
              Couleur
            </option>
            {textColors.map((color) => (
              <option key={color.value} value={color.value}>
                {color.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex min-h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-xs font-bold text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
          <FiDroplet />
          Fond
          <select
            defaultValue=""
            onChange={handleBackgroundChange}
            className="bg-transparent text-xs outline-none"
          >
            <option value="" disabled>
              Couleur
            </option>
            {backgroundColors.map((color) => (
              <option key={color.value} value={color.value}>
                {color.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="relative">
        {!hasVisibleContent ? (
          <p className="pointer-events-none absolute left-4 top-4 text-sm text-slate-400 dark:text-slate-500">
            {placeholder}
          </p>
        ) : null}

        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={emitChange}
          onMouseUp={refreshToolbarState}
          onKeyUp={refreshToolbarState}
          onPaste={handlePaste}
          className="prose prose-slate min-h-[260px] max-w-none px-4 py-4 text-sm leading-7 outline-none dark:prose-invert prose-headings:font-bold prose-a:text-sky-600"
        />
      </div>
    </div>
  );
}